import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileAudio, Loader2, ShieldAlert, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const ANALYSES = [
  { key: "vocal_analysis", label: "Vocal analysis (SATB)", desc: "Estimate Soprano/Alto/Tenor/Bass" },
  { key: "instrument_separation", label: "Instrument separation", desc: "Drums, bass, piano, guitar…" },
  { key: "lyrics_transcription", label: "Lyrics transcription", desc: "Timed lyric lines" },
  { key: "chord_detection", label: "Chord detection", desc: "Chord progression timeline" },
  { key: "key_detection", label: "Key detection", desc: "Musical key" },
  { key: "bpm_detection", label: "BPM detection", desc: "Tempo & time signature" },
  { key: "song_structure", label: "Song structure", desc: "Intro, verse, chorus…" },
];

export default function Analyze() {
  const nav = useNavigate();
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [genre, setGenre] = useState("");
  const [selected, setSelected] = useState(ANALYSES.map((a) => a.key));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onFile = (f) => {
    if (!f) return;
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    audio.onloadedmetadata = () => setDuration(audio.duration || 0);
    audio.src = URL.createObjectURL(f);
  };

  const toggle = (key) =>
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) return setError("Please choose an audio file.");
    setLoading(true);
    const form = new FormData();
    form.append("file", file);
    form.append("title", title);
    form.append("artist", artist);
    form.append("genre", genre);
    form.append("duration", duration);
    form.append("analyses", JSON.stringify(selected));
    try {
      const { data } = await api.post("/songs/analyze", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      nav(`/processing/${data.job.id}?song=${data.song.id}`);
    } catch (err) {
      setError(errMsg(err));
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 sw-fade-up">
      <header>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-white">Analyze a Song</h1>
        <p className="mt-1 text-sm text-slate-400">Upload audio and choose what to analyze.</p>
      </header>

      <form onSubmit={submit} className="space-y-6">
        {/* Dropzone */}
        <div
          data-testid="upload-dropzone"
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
          className="cursor-pointer rounded-2xl border-2 border-dashed border-[#2A3342] bg-[#131822] p-8 text-center transition-colors hover:border-cyan-500/50"
        >
          <input
            ref={fileInput}
            data-testid="file-input"
            type="file"
            accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
            className="hidden"
            onChange={(e) => onFile(e.target.files[0])}
          />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileAudio className="text-cyan-400" size={28} />
              <div className="text-left">
                <div className="font-semibold text-white">{file.name}</div>
                <div className="font-mono-sw text-xs text-slate-400">
                  {(file.size / 1024 / 1024).toFixed(1)} MB{duration ? ` · ${Math.round(duration)}s` : ""}
                </div>
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); setDuration(0); }} className="ml-2 rounded-full p-1 text-slate-500 hover:text-red-400">
                <X size={16} />
              </button>
            </div>
          ) : (
            <>
              <UploadCloud className="mx-auto mb-2 text-slate-500" size={34} />
              <p className="font-semibold text-white">Drop an audio file or tap to browse</p>
              <p className="mt-1 font-mono-sw text-xs text-slate-500">MP3, WAV, M4A, AAC, OGG, FLAC · max 50MB</p>
            </>
          )}
        </div>

        {/* Metadata */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Song title" required>
            <input data-testid="song-title" required value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="Song title" />
          </Field>
          <Field label="Artist">
            <input data-testid="song-artist" value={artist} onChange={(e) => setArtist(e.target.value)} className={inputCls} placeholder="Artist" />
          </Field>
          <Field label="Genre (optional)">
            <input data-testid="song-genre" value={genre} onChange={(e) => setGenre(e.target.value)} className={inputCls} placeholder="e.g. Worship, Pop" />
          </Field>
        </div>

        {/* Analysis options */}
        <div>
          <h3 className="mb-2 font-display text-base font-bold text-white">Select analyses</h3>
          <div className="grid gap-2 sm:grid-cols-2">
            {ANALYSES.map((a) => {
              const on = selected.includes(a.key);
              return (
                <button
                  type="button"
                  key={a.key}
                  data-testid={`analysis-opt-${a.key}`}
                  onClick={() => toggle(a.key)}
                  className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
                    on ? "border-cyan-500/50 bg-cyan-500/5" : "border-[#222A38] bg-[#131822]"
                  }`}
                >
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${on ? "border-cyan-400 bg-cyan-400 text-[#0B0E14]" : "border-[#3A4659]"}`}>
                    {on && "✓"}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-white">{a.label}</span>
                    <span className="block text-xs text-slate-500">{a.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Rights notice (informational, non-blocking) */}
        <div className="flex items-start gap-2 rounded-xl border border-[#222A38] bg-[#131822] p-3 text-xs text-slate-400">
          <ShieldAlert size={15} className="mt-0.5 shrink-0 text-slate-500" />
          Only upload audio you own or have permission to use. SONGWISE AI is for user-authorized material.
        </div>

        {error && <div data-testid="analyze-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>}

        <button data-testid="start-analysis-btn" type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-3.5 font-semibold text-[#0B0E14] transition-colors hover:bg-cyan-400 disabled:opacity-60">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <UploadCloud size={18} />}
          {loading ? "Uploading…" : "Start Analysis"}
        </button>
      </form>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-[#222A38] bg-[#0B0E14] px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-cyan-500/60";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono-sw text-[11px] uppercase tracking-wider text-slate-400">
        {label}{required && <span className="text-cyan-400"> *</span>}
      </span>
      {children}
    </label>
  );
}
